import { useForm } from '@conform-to/react'
import { parseWithZod } from '@conform-to/zod'
import { useFetcher, useLocation } from '@remix-run/react'
import { useTranslation } from 'react-i18next'
import { useHydrated } from 'remix-utils/use-hydrated'
import { wishlistActionI18next } from '#app/components/common/wishlist-action.tsx'
import {
	REMOVE_FROM_WISHLIST_FORM,
	REMOVE_FROM_WISHLIST_INPUTS,
	removeFromWishlistSchema,
} from '#app/components/forms/remove-from-wishlist-form/remove-from-wishlist-form.tsx'
import { type GetProduct } from '#app/models/ecommerce-providers/sylius/shop/fetch/product/get-product.ts'
import { ROOT_INPUTS } from '#app/routes/_layout/route-config.ts'

type UseRemoveFromWishlistProps = {
	product: GetProduct
}

function useRemoveFromWishlistForm({ product }: UseRemoveFromWishlistProps) {
	const { t } = useTranslation(wishlistActionI18next.uiWishlistAction)
	const isHydrated = useHydrated()
	const location = useLocation()

	const fetcherKey = REMOVE_FROM_WISHLIST_FORM.generateFetcherKey!({
		id: product.slug!,
	})
	const fetcher = useFetcher({
		key: fetcherKey,
	})

	const formValues = {
		[ROOT_INPUTS.subaction]: REMOVE_FROM_WISHLIST_FORM.subaction,
		[ROOT_INPUTS.noJs]: String(!isHydrated),
		[ROOT_INPUTS.redirect]: location.pathname,
	}
	const [form, fields] = useForm({
		defaultValue: {
			[REMOVE_FROM_WISHLIST_INPUTS.productId]: product.id,
		},
		onValidate({ formData }) {
			return parseWithZod(formData, {
				schema: removeFromWishlistSchema,
			})
		},
		shouldValidate: 'onSubmit',
	})

	return {
		removeFromWishlistFetcherKey: fetcherKey,
		removeFromWishlistFetcher: fetcher,
		removeFromWishlistFormValues: formValues,
		removeFromWishlistForm: form,
		removeFromWishlistFields: fields,
		t,
	}
}

export { type UseRemoveFromWishlistProps, useRemoveFromWishlistForm }
